import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  LoadingSpinner,
  SettingsPage,
  SideDrawer,
  ContentCentered,
} from '@sales-center/components'

import { useDialogState } from '@user/utils/useDialogState'
import { EmptyState } from './EmptyState'
import {
  selectAllAssignmentRules,
  useGetAssignmentRulesOnMount,
} from '@user/store/leadAssignmentRule'

import { FullScreenDialogWrapper } from './LeadAssignmentRuleForm'
import { useSelector } from 'react-redux'
import { LeadAssignmentRuleList } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleList'
import { useGetAssignmentRule } from './LeadAssignmentRuleList/hooks/useGetAssignmentRule'
import { useGetBranchList } from '@user/store/branches'
import { useGetLocationTypesOnMount } from '@user/store/locationTypes'
import { useGetCampaignsOnMount } from '@user/store/campaigns'
import { useGetDivisionsOnMount } from '@user/store/divisions'
import { useGetSourcesOnMount } from '@user/store/sources'
import { useGetUserListOnMount } from '@user/store/users'
import { useGetStatesOnMount } from '@user/store/dictionaries'
import { useGetTargetPestsOnMount } from '@user/store/targetPests'
import { useGetTerritoriesOnMount } from '@user/store/territory'
import {
  clearTerritoryDraw,
  selectDraftedTerritory,
  selectParkedCondition,
  selectParkedRule,
  selectParkedRuleId,
  selectShouldResumeRule,
  selectTerritoryDrawOnMap,
} from '@user/store/territoryDraw'
import { resumeRuleWithDraftedTerritory } from './territoryConditionDraft'
import { LeadAssignmentRuleDrawer } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleInfoDrawer'

export function LeadAssignmentRule() {
  const [open, onOpen, onClose] = useDialogState()
  const [viewOpen, onViewOpen, onViewClose] = useDialogState()
  const [id, setId] = useState<number | undefined>()

  const { loading } = useGetAssignmentRulesOnMount()
  const assignments = useSelector(selectAllAssignmentRules)
  const { assignmentRule } = useGetAssignmentRule(id)

  useGetBranchList(true)
  useGetLocationTypesOnMount()
  useGetCampaignsOnMount()
  useGetDivisionsOnMount()
  useGetSourcesOnMount()
  useGetUserListOnMount()
  useGetStatesOnMount()
  useGetTargetPestsOnMount()
  // Names for a Territory condition (WWM-17508). No-ops when the tenant does not have the
  // feature, in which case the dropdown does not offer Territory either.
  useGetTerritoriesOnMount()

  // Drawing a territory for a rule (WWM-17508). The admin leaves this form for the map and
  // comes back; the form is parked in the store in between, because the map is a dialog over
  // the whole app and this page stays mounted behind it.
  const dispatch = useDispatch()
  const onMap = useSelector(selectTerritoryDrawOnMap)
  const shouldResume = useSelector(selectShouldResumeRule)
  const parkedRule = useSelector(selectParkedRule)
  const parkedCondition = useSelector(selectParkedCondition)
  const parkedRuleId = useSelector(selectParkedRuleId)
  const drafted = useSelector(selectDraftedTerritory)

  /**
   * Whether the parked form is THIS form's.
   *
   * Scoped by rule id, because a park that outlived its session used to be applied to whatever
   * rule was opened next -- you would open one rule and see another's territory, and the
   * underlying rule was never touched, so nothing in the data hinted at why.
   */
  const parkBelongsHere =
    shouldResume && (parkedRuleId ?? null) === (id ?? null)

  // Out of the way while they draw: the rule form is full-screen, and leaving it up would put
  // it over the map they were just sent to.
  useEffect(() => {
    if (onMap) onClose()
  }, [onMap, onClose])

  // And back again when the trip ends, drawn or abandoned -- but only for the form it left.
  useEffect(() => {
    if (parkBelongsHere) onOpen()
  }, [parkBelongsHere, onOpen])

  // The form to reopen with: what they had, plus the territory they just drew folded into its
  // Territory condition as an unsaved chip. Null the rest of the time, when `editValues` is
  // whichever rule they picked from the list.
  const resumedRule = useMemo(
    () =>
      parkBelongsHere && parkedRule
        ? resumeRuleWithDraftedTerritory(parkedRule, parkedCondition, drafted)
        : null,
    [parkBelongsHere, parkedRule, parkedCondition, drafted],
  )

  const onClick = React.useCallback(
    (ruleId?: number) => {
      // Opening a rule ends any editing session that was parked for a different one. Without
      // this a stale park would sit in the store for the rest of the session, matching nothing
      // and reopening itself.
      if (parkedRule && parkedRuleId !== (ruleId ?? null)) {
        dispatch(clearTerritoryDraw())
      }

      setId(ruleId)
      onOpen()
    },
    [onOpen, setId, dispatch, parkedRule, parkedRuleId],
  )
  const onView = React.useCallback(
    (id?: number) => {
      setId(id)
      onViewOpen()
    },
    [onViewOpen, setId],
  )

  const handleViewClose = React.useCallback(() => {
    setId(undefined)
    onViewClose()
  }, [setId, onViewClose])

  const onEditRule = React.useCallback(() => {
    onViewClose()
    onOpen()
  }, [onViewClose, onOpen])

  const handleClose = React.useCallback(() => {
    setId(undefined)
    onClose()
    // Closing the form abandons the trip to the map with it. Nothing to clean up server-side:
    // the drawn shape was never saved as a territory, which is the point of creating it only
    // when the rule is.
    dispatch(clearTerritoryDraw())
  }, [setId, onClose, dispatch])

  const showForm = useMemo(
    () => !id || (id && assignmentRule),
    [id, assignmentRule],
  )

  return (
    <SettingsPage title="Assignment Rules" padding="40px">
      <ContentCentered>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {!!assignments.length && (
              <LeadAssignmentRuleList
                assignments={assignments}
                onClick={onClick}
                onView={onView}
              />
            )}
            {!assignments.length && <EmptyState onClick={onClick} />}

            {showForm && (
              <FullScreenDialogWrapper
                // Remounts when resuming, so Formik re-initialises from the parked values
                // rather than keeping the state it had before the trip to the map.
                key={resumedRule ? 'resumed' : 'fresh'}
                open={open}
                onClose={handleClose}
                editValues={resumedRule ?? assignmentRule}
                // A resumed NEW rule arrives with values too, so the screen has to be told
                // which it is rather than inferring it from their presence.
                isEdit={!!assignmentRule}
              />
            )}
          </>
        )}
      </ContentCentered>

      <SideDrawer
        title="Assignment Rule"
        onClose={handleViewClose}
        open={viewOpen}
        noTextTransform
        width={450}
        titleVariant={'h6'}
      >
        {assignmentRule && (
          <LeadAssignmentRuleDrawer
            onClose={handleViewClose}
            onEdit={onEditRule}
            assignment={assignmentRule}
          />
        )}
      </SideDrawer>
    </SettingsPage>
  )
}
